import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getAuth, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth as firebaseAuth } from '../firebase';
import { loginWithToken, getMe } from '../services/authService';
import { track } from '../services/analyticsService';

const AuthContext = createContext();

// Session auto-expires after 12 hours of no user activity. Tracked in
// localStorage so the check survives tab reloads. Bypassed entirely when
// the user opted into "Stay logged in on this device" at login time.
const INACTIVITY_TIMEOUT_MS = 12 * 60 * 60 * 1000;
const ACTIVITY_KEY = 'last_activity_at';
const SESSION_EXPIRED_FLAG = 'session_expired_at';
const STAY_LOGGED_IN_KEY = 'stay_logged_in';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [user, setUser] = useState(null);          // Backend user
  const [account, setAccount] = useState(null);     // Backend account
  const [membership, setMembership] = useState(null);
  const [authStatus, setAuthStatus] = useState(firebaseAuth ? 'loading' : 'unauthenticated');
  const [idToken, setIdToken] = useState(null);

  // Listen to Firebase auth state (skip if Firebase not configured)
  useEffect(() => {
    if (!firebaseAuth) return;
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (fbUser) {
        setFirebaseUser(fbUser);
        const token = await fbUser.getIdToken();
        setIdToken(token);
        localStorage.setItem('firebase_token', token);
      } else {
        setFirebaseUser(null);
        setIdToken(null);
        setUser(null);
        setAccount(null);
        setMembership(null);
        setAuthStatus('unauthenticated');
        localStorage.removeItem('firebase_token');
      }
    });
    return () => unsubscribe();
  }, []);

  // When we have a Firebase token, check backend registration
  useEffect(() => {
    if (!idToken) return;

    const checkBackend = async () => {
      try {
        const data = await loginWithToken(idToken);
        setUser(data.user);
        setAccount(data.account);
        setMembership(data.membership);
        setAuthStatus(data.status === 'approved' ? 'approved' : data.status);
        if (data.status === 'approved') track('login');
      } catch (err) {
        if (err.response?.status === 404) {
          setAuthStatus('not_registered');
        } else {
          setAuthStatus('unauthenticated');
        }
      }
    };
    checkBackend();
  }, [idToken]);

  const refreshAuth = useCallback(async () => {
    if (!idToken) return;
    try {
      const data = await getMe();
      setUser(data.user);
      setAccount(data.account);
      setMembership(data.membership);
      setAuthStatus(data.account?.status === 'approved' ? 'approved' : data.account?.status || 'no_account');
    } catch (err) {
      console.error('Failed to refresh auth:', err);
    }
  }, [idToken]);

  const signOut = useCallback(async ({ expired = false } = {}) => {
    const auth = getAuth();
    await firebaseSignOut(auth);
    setUser(null);
    setAccount(null);
    setMembership(null);
    setAuthStatus('unauthenticated');
    localStorage.removeItem('firebase_token');
    localStorage.removeItem('currentLocationId');
    localStorage.removeItem(ACTIVITY_KEY);
    if (!expired) {
      // Explicit sign-out clears the persistence preference.
      localStorage.removeItem(STAY_LOGGED_IN_KEY);
    }
    if (expired) {
      sessionStorage.setItem(SESSION_EXPIRED_FLAG, String(Date.now()));
    } else {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    }
  }, []);

  // Inactivity logout — only runs while the user is actively signed in,
  // and is skipped entirely when "Stay logged in" was selected at login.
  useEffect(() => {
    if (authStatus !== 'approved') return;
    if (localStorage.getItem(STAY_LOGGED_IN_KEY) === 'true') return;

    const checkIdle = () => {
      const last = Number(localStorage.getItem(ACTIVITY_KEY)) || Date.now();
      if (Date.now() - last > INACTIVITY_TIMEOUT_MS) {
        signOut({ expired: true });
        return true;
      }
      return false;
    };

    // Fire once on mount in case the tab was left open past the timeout.
    if (checkIdle()) return;

    const markActive = () => {
      localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    };
    markActive();

    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, markActive, { passive: true })
    );
    const interval = setInterval(checkIdle, 60 * 1000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkIdle();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive));
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [authStatus, signOut]);

  const isAdmin = user?.is_superadmin || membership?.role === 'owner' || membership?.role === 'admin';
  const needsSetup = isAdmin && account?.setup_completed === false && authStatus === 'approved';

  const canViewPage = useCallback((page) => {
    if (!membership) return true;
    if (user?.is_superadmin) return true;
    if (membership.role === 'owner' || membership.role === 'admin') return true;
    const perms = membership.page_permissions;
    if (!perms) return true; // null = all access
    return perms[page] !== false;
  }, [membership, user]);

  return (
    <AuthContext.Provider value={{
      firebaseUser,
      user,
      account,
      membership,
      authStatus,
      idToken,
      isAdmin,
      needsSetup,
      canViewPage,
      signOut,
      refreshAuth,
      setUser,
      setAccount,
      setMembership,
      setAuthStatus,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
